import { Component, Input, OnInit } from '@angular/core';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { StatusService } from '@core/services/status.service';
import { CancelApplicationFormGroup, CancelApplicationModalResponse, CancelReasons } from '@core/typings/ui/cancel-application.typing';
import { AwardService } from '@features/awards/award.service';
import { EmailService } from '@features/system-emails/email.service';
import { EmailNotificationType } from '@features/system-emails/email.typing';
import { AnalyticsService, EventType } from '@yourcause/common/analytics';
import { TypeSafeFormBuilder, TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { I18nService } from '@yourcause/common/i18n';
import { YCModalComponent } from '@yourcause/common/modals';

@Component({
    selector: 'gc-cancel-application-moodal',
    templateUrl: './cancel-application-modal.component.html',
    styleUrls: ['./cancel-application-modal.component.scss'],
    standalone: false
})
export class CancelApplicationModalComponent extends YCModalComponent<CancelApplicationModalResponse> implements OnInit {
  @Input() applicationId: number;
  @Input() programId: number;
  @Input() isNomination: boolean;
  @Input() programName: string;
  @Input() currentlyDraftStatus = false;

  emailType: EmailNotificationType;
  emailActive = false;
  modalHeader: string;
  modalSubheader: string;
  primaryButtonText: string;
  isManager = false;
  customMessageLabel: string;
  reasonSelectOptions: TypeaheadSelectOption<CancelReasons>[];
  formGroup: TypeSafeFormGroup<CancelApplicationFormGroup>;
  canUpdateStatus: boolean;
  submitting = false;

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private i18n: I18nService,
    private statusService: StatusService,
    private analyticsService: AnalyticsService,
    private awardService: AwardService,
    private spinnerService: SpinnerService,
    private emailService: EmailService,
    private portal: PortalDeterminationService
  ) {
    super();
  }

  async ngOnInit () {
    // We only need to check this in manager portal. In applicant, we don't show action if awards exist
    if (this.portal.isManager) {
      this.emailType = this.isNomination ?
        EmailNotificationType.YourNominationIsCanceled :
        EmailNotificationType.YourApplicationIsCanceled;

      this.spinnerService.startSpinner();
      this.emailActive = await this.emailService.isProgramEmailActive(
        this.emailType,
        this.programId
      );
      this.canUpdateStatus = await this.awardService.canUpdateAppStatus(this.applicationId);
      this.spinnerService.stopSpinner();
    } else {
      this.canUpdateStatus = true;
    }
    if (this.canUpdateStatus) {
      this.formGroup = this.formBuilder.group<CancelApplicationFormGroup>({
        reason: CancelReasons.Other,
        comment: '',
        notifyApplicant: true,
        customMessage: '',
        clientEmailTemplateId: 0,
        attachments: [[]],
        ccEmails: [[]],
        bccEmails: [[]]
      });
      this.reasonSelectOptions = this.statusService.getCancelApplicationTypeaheadOptions();
    }
    this.setModalText();
  }

  setModalText () {
    this.modalHeader = this.i18n.translate(
      this.isNomination ?
      'common:hdrCancelNomination' :
      'common:hdrCancelApplication',
      {},
      this.isNomination ?
      'Cancel Nomination' :
      'Cancel Application'
    );
    this.modalSubheader = this.i18n.translate(
      this.isNomination ?
        'GLOBAL:textNominationID' :
        'common:textApplicationId',
      {},
      this.isNomination ?
        'Nomination ID' :
        'Application ID'
    ) + `: ${this.applicationId}`;
    this.primaryButtonText = this.i18n.translate(
      this.isNomination ?
      'common:textCancelNomination' :
      'common:textCancelApplication',
      {},
      this.isNomination ?
      'Cancel nomination' :
      'Cancel application'
    );
    this.customMessageLabel = this.i18n.translate(
      this.isNomination ?
      'common:textCustomMessageToNominator' :
      'MANAGE:textCustomMessageToApplicant',
      {},
      this.isNomination ?
      'Custom message to nominator' :
      'Custom message to applicant'
    );
  }


  async onPrimaryClick () {
    if (!this.canUpdateStatus) {
      this.closeModal.emit();
    } else {
      this.submitting = true;
      this.spinnerService.startSpinner();
      const formData = this.formGroup.value;
      const emailOptions = await this.emailService.getEmailOptionsModelForSave(
        formData,
        this.applicationId
      );
      this.submitting = false;
      this.spinnerService.stopSpinner();
      if (!!emailOptions) {
        this.closeModal.emit({
          applicationId: this.applicationId,
          reason: formData.reason,
          comment: formData.comment,
          sendEmailToApplicants: formData.notifyApplicant,
          emailOptions
        });
        this.analyticsService.emitEvent({
          eventName: 'Cancel application modal submit',
          eventType: EventType.Click,
          extras: null
        });
      }

    }
  }
}
